// client/src/components/AdminDashboard.js
import React, { useState, useEffect, useRef } from 'react';
import {
  collection,
  getDocs,
  query,
  where,
  getCountFromServer,
  limit,
} from 'firebase/firestore';
import { db, storage } from '../../firebase';
import '../CSS/Dashboard.css';
import AdminNavbar from './AdminNavbar';
import { useNavigate } from 'react-router-dom';

// Graph
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend,
} from 'chart.js';
import { ref, listAll, getDownloadURL, uploadBytes } from 'firebase/storage';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

/**
 * OPTI COUTS FIREBASE — SANS INDEX COMPOSITE
 * - getCountFromServer pour les compteurs (0 lectures de documents).
 * - Pour les listes "en attente" : where(role==...), limit(N), TRI LOCAL sur createdAt desc, puis slice + cache sessionStorage.
 * - Pour le graphique : lecture des users avec cache sessionStorage 30min.
 */

const MAX_DOCS_PER_LIST = 200;      // on lit au plus 200 docs par rôle côté serveur
const MAX_RETURN_PER_LIST = 100;    // on n'affiche que les 100 plus récents après tri local

// ============ Cache sessionStorage ============
const GRAPH_CACHE_KEY = 'admin_dash_graph_v1';
const GRAPH_CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes
const PENDING_AFF_CACHE_KEY = 'admin_dash_pending_aff_v1';
const PENDING_COM_CACHE_KEY = 'admin_dash_pending_com_v1';
const PENDING_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const getSessionCache = (key) => {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Date.now() - (parsed.ts || 0) > (parsed.ttl || 0)) return null;
    return parsed.data;
  } catch { return null; }
};
const setSessionCache = (key, data, ttl) => {
  try { sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), ttl, data })); } catch { /* noop */ }
};

const AdminDashboard = () => {
  const navigate = useNavigate();

  // Stats (carrés)
  const [totalUsers, setTotalUsers] = useState(0);
  const [commercantsCount, setCommercantsCount] = useState(0);
  const [affiliatesCount, setAffiliatesCount] = useState(0);

  // Affiliates EN ATTENTE (role = affiliatepending)
  const [newAffiliates, setNewAffiliates] = useState([]);
  const [loadingNewAff, setLoadingNewAff] = useState(true);

  // commercantpending
  const [pendingCommercants, setPendingCommercants] = useState([]);
  const [loadingPending, setLoadingPending] = useState(true);

  // Upload contrat signé
  const [uploadingUid, setUploadingUid] = useState(null);

  // Modale docs
  const [docsModalOpen, setDocsModalOpen] = useState(false);
  const [docsLoading, setDocsLoading] = useState(false);
  const [selectedAffiliate, setSelectedAffiliate] = useState(null);
  const [docsPreview, setDocsPreview] = useState({ photo: null, kbis: [], rib: [], contrat: [] });

  // Graph
  const [labels, setLabels] = useState([]);
  // eslint-disable-next-line
  const [graphUsersCounts, setGraphUsersCounts] = useState([]);
  const [graphCommercantsCounts, setGraphCommercantsCounts] = useState([]);
  const [graphClientsCounts, setGraphClientsCounts] = useState([]);
  const [graphAffiliatesCounts, setGraphAffiliatesCounts] = useState([]);
  const [graphCashierCounts, setGraphCashierCounts] = useState([]);
  const [loadingGraph, setLoadingGraph] = useState(false);
  const [graphLoaded, setGraphLoaded] = useState(false);

  // --- Quick menu (3 traits) ---
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  useEffect(() => {
    const onClick = (e) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, []);

  // 🔒 Empêche le scroll de l’arrière-plan quand la modale est ouverte (utile iOS)
  useEffect(() => {
    if (docsModalOpen) {
      const prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prevOverflow; };
    }
  }, [docsModalOpen]);

  // Utils dates
  const formatDate = (date) => {
    const d = new Date(date);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };
  const formatDateFRLoose = (value) => {
    if (!value) return '';
    let d = null;
    if (value?.toDate) d = value.toDate();
    else if (value instanceof Date) d = value;
    else {
      const parsed = new Date(value);
      d = isNaN(parsed.getTime()) ? null : parsed;
    }
    if (!d) return String(value);
    return d.toLocaleDateString('fr-FR');
  };
  const getDateRange = (startDate, endDate) => {
    const dates = [];
    const current = new Date(startDate);
    while (current <= endDate) {
      dates.push(formatDate(current));
      current.setDate(current.getDate() + 1);
    }
    return dates;
  };

  // Compteurs carrés — COUNT SERVER-SIDE
  useEffect(() => {
    const run = async () => {
      try {
        const usersCol = collection(db, 'users');
        const [totalSnap, commercantsSnap, affiliatesSnap] = await Promise.all([
          getCountFromServer(usersCol),
          getCountFromServer(query(usersCol, where('role', '==', 'commercant'))),
          getCountFromServer(query(usersCol, where('role', '==', 'affiliates'))),
        ]);

        setTotalUsers(totalSnap.data().count || 0);
        setCommercantsCount(commercantsSnap.data().count || 0);
        setAffiliatesCount(affiliatesSnap.data().count || 0);
      } catch (e) {
        console.error('Erreur chargement compteurs :', e);
      }
    };
    run();
  }, []);

  // Helper : lecture par rôle SANS index composite (limit + tri local + slice)
  // Avec cache sessionStorage pour éviter les relectures Firestore
  const getUsersByRoleCostOptimized = async (roleValue, cacheKey) => {
    // Vérifier le cache sessionStorage
    if (cacheKey) {
      const cached = getSessionCache(cacheKey);
      if (cached) return cached;
    }

    const colRef = collection(db, 'users');
    try {
      const snap = await getDocs(query(colRef, where('role', '==', roleValue), limit(MAX_DOCS_PER_LIST)));
      const arr = snap.docs.map(d => {
        const data = d.data();
        // Sérialiser createdAt pour le cache
        const createdAtRaw = data.createdAt?.toDate ? data.createdAt.toDate().toISOString() : data.createdAt || null;
        return { id: d.id, ...data, createdAt: createdAtRaw };
      });

      // TRI LOCAL (createdAt desc)
      arr.sort((a, b) => {
        const da = a.createdAt ? new Date(a.createdAt) : 0;
        const dbb = b.createdAt ? new Date(b.createdAt) : 0;
        return (dbb || 0) - (da || 0);
      });

      const result = arr.slice(0, MAX_RETURN_PER_LIST);

      // Sauvegarder dans le cache
      if (cacheKey) setSessionCache(cacheKey, result, PENDING_CACHE_TTL_MS);

      return result;
    } catch (e) {
      console.error('Erreur lecture role=', roleValue, e);
      return [];
    }
  };

  // Affiliates EN ATTENTE
  useEffect(() => {
    const loadPendingAffiliates = async () => {
      setLoadingNewAff(true);
      const affiliates = await getUsersByRoleCostOptimized('affiliatepending', PENDING_AFF_CACHE_KEY);
      setNewAffiliates(affiliates);
      setLoadingNewAff(false);
    };
    loadPendingAffiliates();
  }, []);

  // Commerçants EN ATTENTE
  useEffect(() => {
    const loadPending = async () => {
      setLoadingPending(true);
      const arr = await getUsersByRoleCostOptimized('commercantpending', PENDING_COM_CACHE_KEY);
      setPendingCommercants(arr);
      setLoadingPending(false);
    };
    loadPending();
  }, []);

  // Storage helpers
  const getLatestInFolder = async (folderPath) => {
    try {
      const folderRef = ref(storage, folderPath);
      const { items } = await listAll(folderRef);
      if (!items?.length) return null;
      let latest = null;
      let latestTs = -1;
      for (const it of items) {
        const ts = parseInt(it.name.split('_')[0], 10);
        if (!Number.isNaN(ts) && ts > latestTs) {
          latestTs = ts;
          latest = it;
        }
      }
      const target = latest || items[0];
      return await getDownloadURL(target);
    } catch {
      return null;
    }
  };
  const getAllInFolder = async (folderPath) => {
    try {
      const folderRef = ref(storage, folderPath);
      const { items } = await listAll(folderRef);
      const urls = await Promise.all(items.map(it => getDownloadURL(it)));
      return urls;
    } catch {
      return [];
    }
  };

  // Ouvrir la fiche dans AdminUsersViewer
  const openFiche = (user) => {
    navigate('/adminusersviewer', { state: { user } });
  };

  // Modale docs (affiliates)
  const openDocsModal = async (affiliate) => {
    setSelectedAffiliate(affiliate);
    setDocsModalOpen(true);
    setDocsLoading(true);
    const uid = affiliate.id;
    const [photoUrl, kbisUrls, ribUrls, contratUrls] = await Promise.all([
      getLatestInFolder(`${uid}/photo-profil`),
      getAllInFolder(`${uid}/documents/kbis`),
      getAllInFolder(`${uid}/documents/rib`),
      getAllInFolder(`${uid}/documents/contrat`),
    ]);
    setDocsPreview({ photo: photoUrl, kbis: kbisUrls, rib: ribUrls, contrat: contratUrls });
    setDocsLoading(false);
  };
  const closeDocsModal = () => {
    setDocsModalOpen(false);
    setSelectedAffiliate(null);
    setDocsPreview({ photo: null, kbis: [], rib: [], contrat: [] });
  };

  // Import contrat signé
  const handleImportSignedContract = async (uid, file, inputEl) => {
    if (!file) return;
    try {
      setUploadingUid(uid);
      const destRef = ref(storage, `${uid}/documents/contrat/${Date.now()}_${file.name}`);
      await uploadBytes(destRef, file);
      alert('Contrat signé importé avec succès.');
    } catch (e) {
      console.error('Erreur upload contrat signé :', e);
      alert("Erreur lors de l'import du contrat signé.");
    } finally {
      setUploadingUid(null);
      if (inputEl) inputEl.value = '';
      if (docsModalOpen && selectedAffiliate?.id === uid) {
        setDocsLoading(true);
        const contratUrls = await getAllInFolder(`${uid}/documents/contrat`);
        setDocsPreview(prev => ({ ...prev, contrat: contratUrls }));
        setDocsLoading(false);
      }
    }
  };

  // Graph data — lecture des users avec cache sessionStorage 30min
  // Ne se lance PAS automatiquement (sauf si cache valide) pour économiser les lectures
  const loadGraphData = async () => {
    setLoadingGraph(true);
    try {
      const cached = getSessionCache(GRAPH_CACHE_KEY);
      let dailyStats;

      if (cached) {
        dailyStats = cached;
      } else {
        const snap = await getDocs(collection(db, 'users'));
        dailyStats = {};
        snap.forEach((docSnap) => {
          const data = docSnap.data();
          if (!data.createdAt) return;
          let d;
          if (data.createdAt?.toDate) d = data.createdAt.toDate();
          else d = new Date(data.createdAt);
          if (isNaN(d.getTime())) return;
          const dateKey = formatDate(d);
          const role = data.role || 'client';
          if (!dailyStats[dateKey]) dailyStats[dateKey] = { total: 0 };
          dailyStats[dateKey].total++;
          dailyStats[dateKey][role] = (dailyStats[dateKey][role] || 0) + 1;
        });
        setSessionCache(GRAPH_CACHE_KEY, dailyStats, GRAPH_CACHE_TTL_MS);
      }

      const dateKeys = Object.keys(dailyStats).sort();

      if (dateKeys.length === 0) {
        setLabels([]);
        setGraphUsersCounts([]);
        setGraphCommercantsCounts([]);
        setGraphClientsCounts([]);
        setGraphAffiliatesCounts([]);
        setGraphCashierCounts([]);
      } else {
        const today = new Date();
        const todayKey = formatDate(today);
        const minDate = new Date(dateKeys[0]);
        const maxKey = dateKeys[dateKeys.length - 1] > todayKey ? dateKeys[dateKeys.length - 1] : todayKey;
        const maxDate = new Date(maxKey);
        const dateLabels = getDateRange(minDate, maxDate);

        setLabels(dateLabels);
        setGraphUsersCounts(dateLabels.map(d => dailyStats[d]?.total || 0));
        setGraphCommercantsCounts(dateLabels.map(d => dailyStats[d]?.commercant || 0));
        setGraphClientsCounts(dateLabels.map(d => dailyStats[d]?.client || 0));
        setGraphAffiliatesCounts(dateLabels.map(d => dailyStats[d]?.affiliates || 0));
        setGraphCashierCounts(dateLabels.map(d => dailyStats[d]?.cashier || 0));
      }
      setGraphLoaded(true);
    } catch (error) {
      console.error('Erreur chargement graphique :', error);
    } finally {
      setLoadingGraph(false);
    }
  };

  // Auto-load si le cache est encore valide (0 lectures Firestore)
  useEffect(() => {
    if (getSessionCache(GRAPH_CACHE_KEY)) loadGraphData();
    // eslint-disable-next-line
  }, []);

  // Graph options
  const displayData = {
    labels,
    datasets: [
      { label: 'Commerçants', data: graphCommercantsCounts, fill: false, borderColor: 'rgba(255,99,132,1)', tension: 0.1, pointRadius: 3 },
      { label: 'Clients', data: graphClientsCounts, fill: false, borderColor: 'rgba(153,102,255,1)', tension: 0.1, pointRadius: 3 },
      { label: 'Affiliates', data: graphAffiliatesCounts, fill: false, borderColor: 'rgba(54,162,235,1)', tension: 0.1, pointRadius: 3 },
      { label: 'Cashier', data: graphCashierCounts, fill: false, borderColor: 'rgba(255,206,86,1)', tension: 0.1, pointRadius: 3 },
    ],
  };
  const displayOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: true } },
    scales: {
      y: { beginAtZero: true, ticks: { stepSize: 1 }, title: { display: true, text: 'Nombre' } },
      x: { title: { display: true, text: 'Date' } },
    },
  };

  return (
    <div className="dashboard-container">
      <style>{`
        .ad-page { width: 100%; display: flex; flex-direction: column; gap: 24px; }
        .ad-page .eb-page-header { margin-bottom: 0; align-items: center; }
        .dashboard-container .ad-page .eb-page-title { margin: 0; text-align: left; }

        /* KPI */
        .ad-kpis { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 200px), 1fr)); gap: 16px; }
        .ad-kpi { background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); box-shadow: var(--eb-shadow-sm); padding: 20px 22px; display: flex; flex-direction: column; gap: 6px; }
        .ad-kpi__label { margin: 0; font-size: 13.5px; font-weight: 600; color: var(--eb-muted); }
        .ad-kpi__value { margin: 0; font-size: 30px; font-weight: 700; letter-spacing: -0.02em; color: var(--eb-text); text-align: left; line-height: 1.1; }

        /* Sections / cartes */
        .ad-card { background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius-lg); box-shadow: var(--eb-shadow-sm); padding: 20px 24px; min-width: 0; }
        .ad-card__head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 16px; }
        .ad-card__title { margin: 0; font-size: 16.5px; font-weight: 650; color: var(--eb-text); text-align: left; }
        .ad-empty { margin: 0; padding: 20px 8px; text-align: center; color: var(--eb-muted); font-size: 14px; }

        .ad-chart { position: relative; width: 100%; height: clamp(240px, 42vh, 520px); }
        .ad-chart__overlay {
          position: absolute; inset: 0;
          background: var(--eb-surface-2);
          border: 1px dashed var(--eb-border-strong);
          border-radius: var(--eb-radius);
          display: flex; align-items: center; justify-content: center;
          z-index: 5;
        }
        .ad-chart__btn {
          display: flex; flex-direction: column; align-items: center; gap: 10px;
          background: var(--eb-surface); border: 1px solid var(--eb-border); border-radius: var(--eb-radius);
          padding: 18px 28px; cursor: pointer; color: var(--eb-text-2);
          box-shadow: var(--eb-shadow-sm);
          transition: box-shadow 0.15s ease, border-color 0.15s ease;
        }
        .ad-chart__btn:hover { box-shadow: var(--eb-shadow-md); border-color: var(--eb-border-strong); }
        .ad-chart__icon { width: 28px; height: 28px; color: var(--eb-brand); }
        .ad-chart__btn span { font-size: 14px; font-weight: 600; }

        /* Listes en attente */
        .ad-two-col { display: grid; grid-template-columns: 1fr; gap: 16px; }
        @media (min-width: 1000px) { .ad-two-col { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); align-items: start; } }
        .ad-list { display: grid; grid-template-columns: 1fr; gap: 10px; }
        .ad-item { border: 1px solid var(--eb-border); border-radius: var(--eb-radius); padding: 14px 16px; display: flex; flex-direction: column; gap: 10px; background: var(--eb-surface); transition: border-color .15s ease; }
        .ad-item:hover { border-color: var(--eb-border-strong); }
        .ad-item__top { display: flex; align-items: baseline; justify-content: space-between; gap: 10px; flex-wrap: wrap; }
        .ad-item__name { font-weight: 600; color: var(--eb-text); overflow-wrap: anywhere; }
        .ad-item__date { font-size: 13px; color: var(--eb-muted); }
        .ad-item__details { font-size: 13.5px; line-height: 1.55; color: var(--eb-text-2); display: grid; gap: 2px; }
        .ad-item__details div { white-space: pre-wrap; overflow-wrap: anywhere; }
        .ad-item__details strong { color: var(--eb-muted); font-weight: 600; }
        .ad-item__actions { display: flex; flex-wrap: wrap; gap: 8px; }

        /* Menu rapide */
        .ad-quick { position: relative; }
        .ad-quick__pop {
          position: absolute; right: 0; top: calc(100% + 8px); z-index: 50;
          background: var(--eb-surface); border: 1px solid var(--eb-border);
          border-radius: var(--eb-radius); box-shadow: var(--eb-shadow-lg);
          min-width: 220px; padding: 6px;
        }
        .ad-quick__item {
          width: 100%; text-align: left; background: transparent; border: none;
          padding: 10px 12px; border-radius: var(--eb-radius-sm); cursor: pointer;
          font-size: 14px; color: var(--eb-text-2); min-height: 40px;
        }
        .ad-quick__item:hover { background: var(--eb-surface-2); color: var(--eb-text); }

        /* Modale documents */
        .ad-modal-overlay {
          position: fixed; inset: 0;
          background: rgba(15,35,56,.55); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center;
          z-index: 1000; padding: 16px;
        }
        .ad-modal {
          width: 100%; max-width: 720px;
          background: var(--eb-surface); border-radius: var(--eb-radius-xl); box-shadow: var(--eb-shadow-lg);
          padding: 24px; position: relative;
          max-height: 90vh; max-height: 90dvh; overflow: auto; -webkit-overflow-scrolling: touch;
          display: flex; flex-direction: column; gap: 16px;
        }
        .ad-modal__head { display: flex; align-items: center; justify-content: space-between; gap: 12px; }
        .dashboard-container .ad-modal__title { margin: 0; font-size: 18px; font-weight: 650; text-align: left; overflow-wrap: anywhere; }
        .ad-modal__close {
          flex-shrink: 0; width: 36px; height: 36px; border-radius: var(--eb-radius-sm);
          border: 1px solid var(--eb-border); background: var(--eb-surface); color: var(--eb-muted);
          font-size: 22px; line-height: 1; display: inline-flex; align-items: center; justify-content: center; cursor: pointer;
        }
        .ad-modal__close:hover { background: var(--eb-surface-2); color: var(--eb-text); }
        .ad-docs-grid { display: grid; grid-template-columns: 1fr; gap: 12px; }
        @media (min-width: 700px) { .ad-docs-grid { grid-template-columns: 1fr 1fr; } }
        .ad-doc { border: 1px solid var(--eb-border); border-radius: var(--eb-radius); padding: 14px; background: var(--eb-surface-2); }
        .ad-doc strong { font-size: 13.5px; color: var(--eb-text-2); }
        .ad-doc em { color: var(--eb-muted); font-style: normal; font-size: 13.5px; }
        .ad-thumb { display: block; width: 100%; height: auto; max-height: 65vh; object-fit: contain; border-radius: var(--eb-radius-sm); border: 1px solid var(--eb-border); background: var(--eb-surface); }
        .ad-doc-links a { display: inline-block; margin-right: 12px; margin-bottom: 6px; font-size: 14px; font-weight: 500; word-break: break-word; }
        .ad-loading { display: flex; align-items: center; gap: 10px; color: var(--eb-muted); font-size: 14px; }

        @media (max-width: 600px) {
          .ad-card { padding: 16px; }
          .ad-kpis { grid-template-columns: 1fr 1fr; gap: 12px; }
          .ad-kpi { padding: 16px; }
          .ad-kpi__value { font-size: 24px; }
          .ad-kpi:last-child:nth-child(odd) { grid-column: 1 / -1; }
          .ad-item__actions .eb-btn { flex: 1 1 auto; }
          .ad-chart { height: clamp(220px, 38vh, 420px); }
          .ad-modal { padding: 20px 16px; }
          .ad-quick__pop { right: auto; left: 0; }
        }
      `}</style>

      <AdminNavbar />

      <div className="ad-page">
        <header className="eb-page-header">
          <div>
            <h1 className="eb-page-title">Accueil</h1>
            <p className="eb-page-subtitle">Vue d'ensemble de la plateforme</p>
          </div>

          {/* Quick menu */}
          <div className="ad-quick" ref={menuRef}>
            <button
              className="eb-btn eb-btn--secondary"
              aria-label="Ouvrir le menu rapide"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen(v => !v)}
            >
              <span aria-hidden="true">&#9776;</span> Raccourcis
            </button>
            {menuOpen && (
              <div className="ad-quick__pop" role="menu">
                <button
                  className="ad-quick__item"
                  role="menuitem"
                  onClick={() => { setMenuOpen(false); navigate('/adminroadmap'); }}
                >
                  🗺️ Roadmap
                </button>
                <button
                  className="ad-quick__item"
                  role="menuitem"
                  onClick={() => { setMenuOpen(false); navigate('/admincatalogpage'); }}
                >
                  📕 Catalogue VEYLIE
                </button>
                <button
                  className="ad-quick__item"
                  role="menuitem"
                  onClick={() => { setMenuOpen(false); navigate('/Croquettes'); }}
                >
                  🐶 Croquettes
                </button>
              </div>
            )}
          </div>
        </header>

        {/* Carrés de stats */}
        <div className="ad-kpis">
          <div className="ad-kpi">
            <p className="ad-kpi__label">Utilisateurs</p>
            <p className="ad-kpi__value">{totalUsers}</p>
          </div>
          <div className="ad-kpi">
            <p className="ad-kpi__label">Commerçants</p>
            <p className="ad-kpi__value">{commercantsCount}</p>
          </div>
          <div className="ad-kpi">
            <p className="ad-kpi__label">Affiliates actifs</p>
            <p className="ad-kpi__value">{affiliatesCount}</p>
          </div>
        </div>

        {/* Graphique */}
        <section className="ad-card">
          <div className="ad-card__head">
            <h2 className="ad-card__title">Statistiques — créations de comptes</h2>
          </div>
          <div className="ad-chart">
            {graphLoaded && labels.length > 0 && (
              <Line data={displayData} options={displayOptions} />
            )}
            {graphLoaded && labels.length === 0 && !loadingGraph && (
              <p className="ad-empty" style={{ paddingTop: 40 }}>Aucune donnée à afficher.</p>
            )}
            {!graphLoaded && (
              <div className="ad-chart__overlay">
                {loadingGraph ? (
                  <div className="eb-spinner" />
                ) : (
                  <button className="ad-chart__btn" onClick={loadGraphData}>
                    <svg className="ad-chart__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="23 4 23 10 17 10" />
                      <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
                    </svg>
                    <span>Charger le graphique</span>
                  </button>
                )}
              </div>
            )}
          </div>
        </section>

        {/* 2 colonnes sur desktop : Affiliates en attente + Commerçants en attente */}
        <div className="ad-two-col">
          {/* ===== AFFILIATES EN ATTENTE (en PREMIER) ===== */}
          <section className="ad-card">
            <div className="ad-card__head">
              <h2 className="ad-card__title">Affiliates en attente</h2>
              {loadingNewAff
                ? <span className="eb-spinner eb-spinner--sm" aria-label="Chargement…" />
                : <span className={`eb-badge${newAffiliates.length ? ' eb-badge--warning' : ''}`}>{newAffiliates.length}</span>}
            </div>

            {loadingNewAff ? null : (
              newAffiliates.length === 0 ? (
                <p className="ad-empty">Aucun affiliate en attente.</p>
              ) : (
                <div className="ad-list">
                  {newAffiliates.map(a => {
                    const createdAtDate = a.createdAt?.toDate ? a.createdAt.toDate() : a.createdAt ? new Date(a.createdAt) : null;
                    const displayName =
                      a.pseudo || a.prenom || a.nom ? `${a.pseudo || ''} ${a.prenom || ''} ${a.nom || ''}`.trim() : a.id;
                    const inputId = `import-contrat-${a.id}`;

                    return (
                      <div className="ad-item" key={a.id}>
                        <div className="ad-item__top">
                          <div className="ad-item__name">{displayName}</div>
                          <div className="ad-item__date">
                            {createdAtDate ? `Créé le ${formatDate(createdAtDate)}` : ''}
                          </div>
                        </div>

                        <div className="ad-item__details">
                          <div><strong>Adresse :</strong> {a.adresse || '—'}</div>
                          <div><strong>Date de naissance :</strong> {a.dateNaissance ? formatDateFRLoose(a.dateNaissance) : '—'}</div>
                          <div><strong>Téléphone :</strong> {a.telephone || '—'}</div>
                          {a.note ? <div><strong>Note :</strong> {a.note}</div> : null}
                        </div>

                        <div className="ad-item__actions">
                          <button className="eb-btn eb-btn--secondary eb-btn--sm" onClick={() => openFiche(a)}>Fiche</button>
                          <button className="eb-btn eb-btn--secondary eb-btn--sm" onClick={() => openDocsModal(a)}>Docs</button>

                          <input
                            id={inputId}
                            type="file"
                            accept="application/pdf,image/*"
                            style={{ display: 'none' }}
                            onChange={(e) => handleImportSignedContract(a.id, e.target.files?.[0], e.currentTarget)}
                          />
                          <button
                            className="eb-btn eb-btn--primary eb-btn--sm"
                            onClick={() => document.getElementById(inputId).click()}
                            disabled={uploadingUid === a.id}
                            title="Importer la version finale cosignée"
                          >
                            {uploadingUid === a.id ? 'Import…' : 'Importer contrat signé'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </section>

          {/* ===== COMMERCANTS EN ATTENTE (en DEUXIÈME) ===== */}
          <section className="ad-card">
            <div className="ad-card__head">
              <h2 className="ad-card__title">Commerçants en attente</h2>
              {loadingPending ? (
                <span className="eb-spinner eb-spinner--sm" aria-label="Chargement…" />
              ) : (
                <span className={`eb-badge${pendingCommercants.length ? ' eb-badge--warning' : ''}`}>{pendingCommercants.length}</span>
              )}
            </div>

            {loadingPending ? null : (
              pendingCommercants.length === 0 ? (
                <p className="ad-empty">Aucun compte en attente.</p>
              ) : (
                <div className="ad-list">
                  {pendingCommercants.map(u => {
                    const createdAtDate = u.createdAt?.toDate ? u.createdAt.toDate() : u.createdAt ? new Date(u.createdAt) : null;
                    const displayName =
                      u.pseudo || u.prenom || u.nom ? `${u.pseudo || ''} ${u.prenom || ''} ${u.nom || ''}`.trim() : u.id;

                    return (
                      <div className="ad-item" key={u.id}>
                        <div className="ad-item__top">
                          <div className="ad-item__name">{displayName}</div>
                          <div className="ad-item__date">
                            {createdAtDate ? `Créé le ${formatDate(createdAtDate)}` : ''}
                          </div>
                        </div>

                        <div className="ad-item__details">
                          <div><strong>Adresse :</strong> {u.adresse || '—'}</div>
                          <div><strong>Téléphone :</strong> {u.telephone || '—'}</div>
                          {u.note ? <div><strong>Note :</strong> {u.note}</div> : null}
                        </div>
                        <div className="ad-item__actions">
                          <button className="eb-btn eb-btn--secondary eb-btn--sm" onClick={() => openFiche(u)}>
                            Fiche
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </section>
        </div>
      </div>

      {/* Modale docs */}
      {docsModalOpen && (
        <div className="ad-modal-overlay" onClick={closeDocsModal}>
          <div className="ad-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="ad-modal__head">
              <h3 className="ad-modal__title">
                Documents — {selectedAffiliate ? (selectedAffiliate.pseudo || selectedAffiliate.id) : ''}
              </h3>
              <button type="button" className="ad-modal__close" aria-label="Fermer" onClick={closeDocsModal}>&times;</button>
            </div>
            {docsLoading ? (
              <div className="ad-loading"><span className="eb-spinner eb-spinner--sm" /> Chargement des documents…</div>
            ) : (
              <div className="ad-docs-grid">
                <div className="ad-doc">
                  <strong>Photo de profil</strong>
                  <div style={{ marginTop: 8 }}>
                    {docsPreview.photo ? (
                      <img className="ad-thumb" src={docsPreview.photo} alt="Profil" />
                    ) : (
                      <em>Aucune photo trouvée</em>
                    )}
                  </div>
                </div>

                <div className="ad-doc">
                  <strong>Kbis / INSEE</strong>
                  <div className="ad-doc-links" style={{ marginTop: 8 }}>
                    {docsPreview.kbis.length ? docsPreview.kbis.map((u, i) => (
                      <a key={i} href={u} target="_blank" rel="noreferrer">Voir document {i + 1}</a>
                    )) : <em>Aucun document</em>}
                  </div>
                </div>

                <div className="ad-doc">
                  <strong>RIB signé</strong>
                  <div className="ad-doc-links" style={{ marginTop: 8 }}>
                    {docsPreview.rib.length ? docsPreview.rib.map((u, i) => (
                      <a key={i} href={u} target="_blank" rel="noreferrer">Voir document {i + 1}</a>
                    )) : <em>Aucun document</em>}
                  </div>
                </div>

                <div className="ad-doc">
                  <strong>Contrat signé</strong>
                  <div className="ad-doc-links" style={{ marginTop: 8 }}>
                    {docsPreview.contrat.length ? docsPreview.contrat.map((u, i) => (
                      <a key={i} href={u} target="_blank" rel="noreferrer">Voir document {i + 1}</a>
                    )) : <em>Aucun document</em>}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
